import React, { useState, useCallback } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertColor } from '@mui/material/Alert';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { SlideTransition } from 'modules/common';
import { useSnackbarStyles } from 'modules/common/hooks/useSnackbarStyles';
import { getDesktopVersion, isWithinAnotherApp } from 'containerApp';
import { useMeContext } from 'modules/me';
import { getTokens } from 'modules/oauth/tokenStore';
import { useFeatureFlag } from 'modules/common/hooks';

interface AboutDialogProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    maxWidth: '550px'
  },
  content: {
    paddingBottom: theme.spacing(2)
  },
  actions: {
    paddingBottom: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  textField: {
    width: '100%'
  },
  checkbox: {
    ...theme.typography.h1
  },
  disclaimer: {
    paddingTop: theme.spacing(1)
  },
  title: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.spacing(2.5),
    lineHeight: 1.6,
    fontFamily: theme.typography.h6.fontFamily
  }
}));

export const FeedbackDialog = ({
  open,
  onClose
}: AboutDialogProps): JSX.Element => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [severity, setSeverity] = useState<AlertColor>('success');
  const snackbarClasses = useSnackbarStyles({
    category: severity
  });

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [sendAnalytics, setSendAnalytics] = useState(true);
  const me = useMeContext();

  const handleSend = useCallback(async () => {
    onClose();

    const metadata = {
      feedback: feedback,
      userId: me?.id,
      referrer: window.location.hostname,
      version: window.version,
      desktopVersion: isWithinAnotherApp() ? getDesktopVersion() : undefined,
      sendAnalytics: false
    };

    if (sendAnalytics) {
      metadata.sendAnalytics = true;
    }

    try {
      const tokens = getTokens();
      const resp = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify(metadata)
      });

      if (resp.status !== 200) {
        console.error('error sending feedback', resp.status, resp.statusText);
        setSeverity('error');
      }
    } catch (err) {
      console.error('error sending feedback', err);
      setSeverity('error');
    }

    setConfirmationOpen(true);

    setFeedback('');
  }, [onClose, feedback, me?.id, sendAnalytics]);

  const handleCloseConfirmation = useCallback(() => {
    setConfirmationOpen(false);
  }, [setConfirmationOpen]);

  const handleSetFeedback = useCallback(
    (event) => {
      setFeedback(event.target.value);
    },
    [setFeedback]
  );

  const handleSendAnalyticsChange = useCallback(() => {
    setSendAnalytics(!sendAnalytics);
  }, [sendAnalytics]);
  const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');

  return (
    <>
      <Dialog
        data-testid="feedback-dialog"
        open={open}
        classes={{ paper: classes.dialog }}
        onClose={isTiwVpatEnabled ? onClose : undefined}
      >
        <DialogTitle className={classes.title}>
          <FormattedMessage id="feedbackDialog.feedback" />
        </DialogTitle>
        <DialogContent className={classes.content}>
          <Grid container>
            <Grid item xs={12}>
              <TextField
                placeholder={formatMessage({
                  id: 'feedbackDialog.yourFeedback'
                })}
                variant="outlined"
                multiline={true}
                rows={4}
                className={classes.textField}
                value={feedback}
                onChange={handleSetFeedback}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendAnalytics}
                    onChange={handleSendAnalyticsChange}
                    name="diagnostics"
                    color="primary"
                  />
                }
                label={formatMessage({
                  id: 'feedbackDialog.includeDiagnostics'
                })}
                className={classes.checkbox}
              />
            </Grid>
            <Grid item xs={12} className={classes.disclaimer}>
              <Typography variant="caption" color="textSecondary">
                <FormattedMessage id="feedbackDialog.diagnostics" />
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button onClick={onClose}>
            <Typography color="textSecondary">
              <FormattedMessage id="feedbackDialog.cancel" />
            </Typography>
          </Button>
          <Button
            color="primary"
            variant="contained"
            data-testid="feedback-sent-btn"
            onClick={handleSend}
          >
            <FormattedMessage id="feedbackDialog.send" />
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        open={confirmationOpen}
        autoHideDuration={6000}
        onClose={handleCloseConfirmation}
        className={snackbarClasses.snackBar}
        TransitionComponent={SlideTransition}
      >
        <Alert
          variant="filled"
          severity={severity}
          className={classNames(snackbarClasses.item, snackbarClasses.severity)}
          aria-label={
            isWithinAnotherApp()
              ? formatMessage({ id: `feedbackDialog.${severity}` })
              : undefined
          }
        >
          <Typography variant="body1">
            <FormattedMessage id={`feedbackDialog.${severity}`} />
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};
